import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Skeleton } from '@sorare/core/src/atoms/animations/Skeleton';
import { Centered } from '@sorare/core/src/atoms/layout/Centered';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandM } from '@sorare/core/src/atoms/typography';
import { NeonEdge } from '@sorare/core/src/atoms/ui/NeonEdge';
import { Dialog } from '@sorare/core/src/components/dialog';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useCloseDialog } from '@sorare/core/src/hooks/navigation/useCloseDialog';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { range } from '@sorare/core/src/lib/arrays';
import { isType } from '@sorare/core/src/lib/gql';
import {
  mobileAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import background from 'assets/gems/background.webp';

import { GemsPack } from '../BuyPacks/GemsPack';
import type {
  BuyGemsDialogQuery,
  BuyGemsDialogQueryVariables,
} from './__generated__/index.graphql';

const DialogBody = styled(Vertical)`
  display: flex;
  flex-direction: column;
  text-align: center;
  background: url(${background}) center center / cover no-repeat;
  min-height: 100%;
`;

const Header = styled(Centered)`
  padding: var(--double-unit);
  border-bottom: 1px solid var(--c-nd-150);
  display: grid;
  grid-template-columns: var(--quadruple-unit) 1fr var(--quadruple-unit);
`;
const GemsPackContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fill, calc(50% - var(--unit)));
  gap: var(--unit);
  min-width: min(400px, calc(var(--100vw) - 6 * var(--unit)));
  padding: var(--double-unit);

  .usePurchasableItemId & {
    min-width: unset;
    grid-template-columns: repeat(auto-fit, minmax(115px, 1fr));

    @media ${mobileAndAbove} {
      grid-template-columns: repeat(3, minmax(115px, 1fr));
    }
  }

  padding: var(--double-unit);
  @media ${tabletAndAbove} {
    gap: var(--double-unit);
  }
`;

const GemsPackSkeleton = styled(Skeleton)`
  min-width: 120px;
  height: 210px;
  border-radius: var(--double-unit);
  border: 1px solid var(--c-nd-150);
`;

const QUERY = gql`
  query BuyGemsDialogQuery($sport: Sport!) {
    market {
      id
      gemsPacks: inGameCurrencyPacks(currency: COMMON_GEM) {
        id

        baseAmount
        ...GemsPack_inGameCurrencyPack
      }
    }
    tokens {
      purchasableItems(sport: $sport) {
        slug
        ... on InGameCurrencyPurchasableItem {
          slug
          baseAmount
          ...GemsPack_inGameCurrencyPurchasableItem
        }
      }
    }
  }
  ${GemsPack.fragments.inGameCurrencyPack}
  ${GemsPack.fragments.inGameCurrencyPurchasableItem}
` as TypedDocumentNode<BuyGemsDialogQuery, BuyGemsDialogQueryVariables>;

type Props = {
  onClose?: () => void;
};
export const BuyGemsDialog = ({ onClose: onCloseProps }: Props) => {
  const {
    flags: { usePurchasableItemId = false },
  } = useFeatureFlags();

  const closeDialog = useCloseDialog();
  const onClose = () => {
    closeDialog(DialogKey.buyGems);
    onCloseProps?.();
  };
  const { sport } = useSportContext();
  const { data, loading } = useQuery(QUERY, { variables: { sport } });
  const { market, tokens } = data || {};

  const gemsPacks = usePurchasableItemId
    ? tokens?.purchasableItems
        .map(item =>
          isType(item, 'InGameCurrencyPurchasableItem') ? item : null
        )
        .filter(Boolean) || []
    : market?.gemsPacks;

  const sortedGemsPacks = [...(gemsPacks || [])].sort((a, b) => {
    const aAmount = a.baseAmount;
    const bAmount = b.baseAmount;
    return aAmount - bAmount;
  });

  const SKELETONS = range(usePurchasableItemId ? 6 : 4);
  return (
    <Dialog open hideHeader onClose={onClose}>
      {({ CloseButton }) => (
        <DialogBody className={classNames({ usePurchasableItemId })}>
          <NeonEdge side="top" showNeon="always" color="#8CE456">
            <Header right={<CloseButton onClose={onClose} />}>
              <BrandM bold>
                <FormattedMessage
                  id="GemsPacks.title"
                  defaultMessage="Buy Gems"
                />
              </BrandM>
            </Header>

            <GemsPackContainer>
              {loading
                ? SKELETONS.map(index => <GemsPackSkeleton key={index} />)
                : sortedGemsPacks.map(gemsPack => (
                    <GemsPack
                      key={gemsPack.id}
                      gemsPack={gemsPack}
                      onClose={onClose}
                    />
                  ))}
            </GemsPackContainer>
          </NeonEdge>
        </DialogBody>
      )}
    </Dialog>
  );
};
